import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import TopBar from "@/components/TopBar";
import { api, API_BASE } from "@/lib/api";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { swallow } from "@/lib/log";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, Calendar as CalendarIcon,
  Download, Bell, FileText, ShieldCheck, TrendingUp, Plane,
  Link2, RefreshCw, Unlink,
} from "lucide-react";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const WEEKDAYS_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

const TYPE_META = {
  reminder:   { icon: Bell,        label: "Reminder",   color: "text-orange-500 bg-orange-500/12" },
  document:   { icon: FileText,    label: "Document",   color: "text-emerald-500 bg-emerald-500/12" },
  insurance:  { icon: ShieldCheck, label: "Insurance",  color: "text-sky-500 bg-sky-500/12" },
  investment: { icon: TrendingUp,  label: "Investment", color: "text-emerald-600 bg-emerald-600/12" },
  travel:     { icon: Plane,       label: "Travel",     color: "text-indigo-500 bg-indigo-500/12" },
};

function buildCalendarGrid(year, month) {
  // month is 1-indexed
  const first = new Date(Date.UTC(year, month - 1, 1));
  const firstWeekday = first.getUTCDay(); // 0..6, Sun=0
  const daysInMonth = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const cells = [];
  // Leading blanks
  for (let i = 0; i < firstWeekday; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const iso = `${year}-${String(month).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ day: d, iso });
  }
  // Trailing blanks to complete the last row (multiple of 7)
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatDayLong(iso) {
  const d = new Date(`${iso}T00:00:00Z`);
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", timeZone: "UTC" });
}

export default function CalendarPage() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState(null);
  const [gcalStatus, setGcalStatus] = useState({ configured: false, connected: false });
  const [syncing, setSyncing] = useState(false);
  const [sp] = useSearchParams();

  const todayIso = new Date().toISOString().slice(0, 10);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/calendar/events?year=${year}&month=${month}`);
      setEvents(data.events || []);
    } catch (e) { swallow("Calendar")(e); }
    finally { setLoading(false); }
  };

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/status");
      setGcalStatus(data);
    } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, [year, month]);
  useEffect(() => { loadStatus(); }, []);

  // Handle post-OAuth redirect params (?connected=1 or ?connect_error=...)
  useEffect(() => {
    if (sp.get("connected") === "1") {
      toast.success("Google Calendar connected");
      loadStatus();
    }
    const err = sp.get("connect_error");
    if (err) toast.error(`Google Calendar connect failed: ${err}`);
    // eslint-disable-next-line
  }, [sp]);

  const cells = useMemo(() => buildCalendarGrid(year, month), [year, month]);
  const eventsByDay = useMemo(() => {
    const map = {};
    for (const ev of events) {
      (map[ev.date] = map[ev.date] || []).push(ev);
    }
    return map;
  }, [events]);

  const prevMonth = () => {
    if (month === 1) { setYear((y) => y - 1); setMonth(12); }
    else setMonth((m) => m - 1);
  };
  const nextMonth = () => {
    if (month === 12) { setYear((y) => y + 1); setMonth(1); }
    else setMonth((m) => m + 1);
  };
  const goToday = () => {
    setYear(today.getFullYear());
    setMonth(today.getMonth() + 1);
  };

  const downloadFeed = () => {
    // Download the full .ics feed. Attaches the JWT via a fetch + blob because
    // <a href> can't set Authorization headers.
    (async () => {
      try {
        const token = localStorage.getItem("impo_token");
        const res = await fetch(`${API_BASE}/calendar/reminders.ics`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) throw new Error("Download failed");
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = "impo-calendar.ics";
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success("Calendar file downloaded");
      } catch (e) { toast.error("Download failed"); }
    })();
  };

  const connectGoogle = async () => {
    try {
      const { data } = await api.get("/oauth/calendar/login");
      window.location.href = data.authorization_url;
    } catch (err) {
      const detail = err?.response?.data?.detail || "Google Calendar isn't configured yet.";
      toast.error(detail);
    }
  };

  const disconnectGoogle = async () => {
    try {
      await api.post("/oauth/calendar/disconnect");
      toast.success("Google Calendar disconnected");
      loadStatus();
    } catch { toast.error("Failed to disconnect"); }
  };

  const syncNow = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/calendar/sync");
      toast.success(`Synced ${data.synced} / ${data.total} events to Google`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const selectedEvents = selectedDay ? (eventsByDay[selectedDay] || []) : [];

  return (
    <>
      <TopBar
        title="Calendar"
        subtitle="Everything time-bound in one place"
        back
        showSearch
      />

      <div className="px-4 pt-3 pb-6 space-y-4" data-testid="calendar-page">

        {/* Month header */}
        <div className="flex items-center justify-between gap-2">
          <div className="font-display font-semibold text-lg" data-testid="calendar-month-label">
            {MONTH_NAMES[month - 1]} {year}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={goToday}
              className="h-8 px-3 rounded-full text-xs font-medium border border-border hover:bg-[hsl(var(--accent))]"
              data-testid="calendar-today-button"
            >
              Today
            </button>
            <button
              type="button"
              onClick={prevMonth}
              className="h-8 w-8 rounded-full grid place-items-center hover:bg-[hsl(var(--accent))]"
              aria-label="Previous month"
              data-testid="calendar-prev-month"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={nextMonth}
              className="h-8 w-8 rounded-full grid place-items-center hover:bg-[hsl(var(--accent))]"
              aria-label="Next month"
              data-testid="calendar-next-month"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Weekday header */}
        <div className="grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.14em] text-muted-foreground text-center">
          {WEEKDAYS_SHORT.map((w) => <div key={w}>{w}</div>)}
        </div>

        {/* Day grid */}
        <div className="grid grid-cols-7 gap-1" data-testid="calendar-grid">
          {cells.map((cell, i) => {
            if (!cell) return <div key={`b-${i}`} className="aspect-square" />;
            const dayEvents = eventsByDay[cell.iso] || [];
            const isToday = cell.iso === todayIso;
            const uniqueTypes = Array.from(new Set(dayEvents.map((e) => e.type))).slice(0, 4);
            return (
              <button
                key={cell.iso}
                type="button"
                onClick={() => setSelectedDay(cell.iso)}
                className={cn(
                  "aspect-square rounded-xl border p-1.5 flex flex-col items-center justify-between transition text-xs relative",
                  isToday
                    ? "border-primary bg-primary/10"
                    : dayEvents.length > 0
                      ? "border-border bg-card hover:bg-[hsl(var(--accent))]"
                      : "border-transparent bg-transparent hover:bg-[hsl(var(--accent))]",
                )}
                data-testid={`calendar-day-${cell.iso}`}
                aria-label={`${cell.iso}${dayEvents.length ? `, ${dayEvents.length} events` : ""}`}
              >
                <div className={cn(
                  "font-medium tabular-nums self-start",
                  isToday && "text-primary font-bold"
                )}>{cell.day}</div>
                <div className="flex items-center gap-0.5">
                  {uniqueTypes.map((t) => {
                    const meta = TYPE_META[t];
                    if (!meta) return null;
                    return (
                      <span key={t} className={cn("h-1.5 w-1.5 rounded-full", meta.dot)} />
                    );
                  })}
                </div>
              </button>
            );
          })}
        </div>

        {/* Legend */}
        {events.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 text-[10px] text-muted-foreground">
            {Object.entries(TYPE_META).map(([k, m]) => (
              <span key={k} className="inline-flex items-center gap-1">
                <span className={cn("h-1.5 w-1.5 rounded-full", m.dot)} />
                {m.label}
              </span>
            ))}
          </div>
        )}

        {loading && events.length === 0 && (
          <div className="text-[11px] text-muted-foreground text-center">Loading calendar…</div>
        )}

        {/* Google Calendar sync card */}
        <div className="rounded-2xl border border-border bg-card p-4 space-y-3" data-testid="calendar-gcal-card">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl grid place-items-center bg-primary/12 text-primary shrink-0">
              <CalendarIcon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-display font-semibold text-sm">Sync with your calendar</div>
              <div className="text-xs text-muted-foreground mt-0.5">
                {gcalStatus.configured
                  ? (gcalStatus.connected
                      ? "Your Impo events are being pushed to Google Calendar."
                      : "Two-way sync with Google Calendar. Reminders + expiries become real events.")
                  : "Add your Google Calendar credentials in backend .env to enable OAuth sync. Meanwhile, download an .ics file below — Google Calendar, Apple Calendar and Outlook all support it."}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {gcalStatus.configured && !gcalStatus.connected && (
              <Button onClick={connectGoogle} className="gap-1.5" data-testid="calendar-connect-google">
                <Link2 className="h-4 w-4" /> Connect Google Calendar
              </Button>
            )}
            {gcalStatus.configured && gcalStatus.connected && (
              <>
                <Button onClick={syncNow} disabled={syncing} className="gap-1.5" data-testid="calendar-sync-now">
                  <RefreshCw className={cn("h-4 w-4", syncing && "animate-spin")} /> {syncing ? "Syncing…" : "Sync now"}
                </Button>
                <Button variant="outline" onClick={disconnectGoogle} className="gap-1.5" data-testid="calendar-disconnect-google">
                  <Unlink className="h-4 w-4" /> Disconnect
                </Button>
                <Badge variant="secondary" className="rounded-full">Connected</Badge>
              </>
            )}
            <Button variant="outline" onClick={downloadFeed} className="gap-1.5" data-testid="calendar-download-ics">
              <Download className="h-4 w-4" /> Download .ics
            </Button>
            {!gcalStatus.configured && (
              <Badge variant="secondary" className="rounded-full">Google sync coming soon</Badge>
            )}
          </div>
        </div>

        {/* Empty state helper */}
        {!loading && events.length === 0 && (
          <EmptyState
            icon={CalendarIcon}
            title="Nothing time-bound this month"
            subtitle="Add reminders, insurance renewals, trips or document expiries and they’ll show up here."
          />
        )}
      </div>

      {/* Day drawer */}
      <Dialog open={!!selectedDay} onOpenChange={(v) => !v && setSelectedDay(null)}>
        <DialogContent className="rounded-2xl max-w-[92vw] md:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">{selectedDay ? formatDayLong(selectedDay) : ""}</DialogTitle>
          </DialogHeader>
          {selectedEvents.length === 0 && (
            <div className="text-sm text-muted-foreground">Nothing scheduled.</div>
          )}
          <div className="space-y-2">
            {selectedEvents.map((ev) => {
              const meta = TYPE_META[ev.type] || TYPE_META.reminder;
              const Icon = meta.icon;
              return (
                <div
                  key={`${ev.type}-${ev.id}-${ev.subtype || "main"}`}
                  className="rounded-xl border border-border p-3 flex items-start gap-3"
                  data-testid={`calendar-event-${ev.type}-${ev.id}`}
                >
                  <div className={cn("h-9 w-9 rounded-xl grid place-items-center shrink-0", meta.color)}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-semibold text-sm truncate">{ev.title}</div>
                    {ev.subtitle && <div className="text-xs text-muted-foreground truncate">{ev.subtitle}</div>}
                  </div>
                  <a
                    href={`${API_BASE}/calendar/${ev.type}/${ev.id}.ics`}
                    className="h-8 w-8 rounded-full grid place-items-center hover:bg-[hsl(var(--accent))]"
                    onClick={async (e) => {
                      e.preventDefault();
                      try {
                        const token = localStorage.getItem("impo_token");
                        const res = await fetch(`${API_BASE}/calendar/${ev.type}/${ev.id}.ics`, {
                          headers: { Authorization: `Bearer ${token}` },
                        });
                        if (!res.ok) throw new Error();
                        const blob = await res.blob();
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `impo-${ev.type}-${ev.id.slice(0, 8)}.ics`;
                        document.body.appendChild(a); a.click(); a.remove();
                        URL.revokeObjectURL(url);
                      } catch { toast.error("Download failed"); }
                    }}
                    aria-label="Download .ics"
                    data-testid={`calendar-event-ics-${ev.id}`}
                  >
                    <Download className="h-4 w-4" />
                  </a>
                </div>
              );
            })}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedDay(null)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
